"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getSession } from "next-auth/react";
import {
  AlertTriangle,
  Bug,
  ChevronDown,
  Home,
  RefreshCcw,
  Terminal,
} from "lucide-react";

type ErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function Error({ error, reset }: ErrorProps) {
  const pathname = usePathname();
  const homePath = pathname === "/fa" || pathname.startsWith("/fa/") ? "/fa" : "/en";
  const [showDetails, setShowDetails] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const canShowDiagnostics = process.env.NODE_ENV !== "production" || isAdmin;

  useEffect(() => {
    let active = true;

    getSession()
      .then((session) => {
        if (!active) return;
        setIsAdmin(session?.user?.role === "ADMIN");
      })
      .catch(() => {
        if (!active) return;
        setIsAdmin(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const errorDetails = useMemo(
    () =>
      [
        `Name: ${error.name || "Error"}`,
        `Message: ${error.message || "No error message was provided."}`,
        error.digest ? `Digest: ${error.digest}` : null,
        error.stack ? `Stack:\n${error.stack}` : null,
      ]
        .filter(Boolean)
        .join("\n\n"),
    [error],
  );

  const logErrorToConsole = () => {
    console.group("Route error");
    console.error(error);
    if (error.digest) console.info("Digest:", error.digest);
    console.groupEnd();
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 text-slate-950 sm:px-6 lg:px-8">
      <section className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center">
        <div className="grid w-full gap-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:grid-cols-[1fr_320px] lg:p-10">
          <div className="flex flex-col justify-center">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-lg bg-red-50 text-red-600 ring-1 ring-red-100">
              <AlertTriangle aria-hidden="true" size={26} />
            </div>

            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-red-600">
              Page error
            </p>
            <h1 className="max-w-2xl text-3xl font-semibold tracking-normal text-slate-950 sm:text-4xl">
              Something went wrong
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              The page failed while loading. You can try again, or return to the
              homepage and continue from there.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={reset}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
              >
                <RefreshCcw aria-hidden="true" size={18} />
                Try again
              </button>
              <Link
                href={homePath}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
              >
                <Home aria-hidden="true" size={18} />
                Go home
              </Link>
            </div>

            {canShowDiagnostics ? (
              <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-amber-950">
                      Admin diagnostics
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => setShowDetails((value) => !value)}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-3.5 py-2 text-sm font-semibold text-amber-950 transition hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
                      aria-expanded={showDetails}
                    >
                      <Bug aria-hidden="true" size={16} />
                      {showDetails ? "Hide error" : "Show error"}
                      <ChevronDown
                        aria-hidden="true"
                        size={16}
                        className={showDetails ? "rotate-180 transition" : "transition"}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={logErrorToConsole}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-3.5 py-2 text-sm font-semibold text-amber-950 transition hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    >
                      <Terminal aria-hidden="true" size={16} />
                      Show console
                    </button>
                  </div>
                </div>

                {showDetails ? (
                  <pre className="mt-4 max-h-80 overflow-auto rounded-md border border-amber-200 bg-slate-950 p-4 text-xs leading-6 text-slate-100 whitespace-break-spaces">
                    {errorDetails}
                  </pre>
                ) : null}
              </div>
            ) : null}
          </div>

          <aside className="rounded-lg border border-slate-200 bg-slate-50 p-5">
            <p className="text-sm font-semibold text-slate-950">
              What you can do
            </p>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
              <li className="rounded-md bg-white p-3 ring-1 ring-slate-200">
                Retry the request if this was a temporary loading issue.
              </li>
              <li className="rounded-md bg-white p-3 ring-1 ring-slate-200">
                Return home if the page keeps failing.
              </li>
              <li className="rounded-md bg-white p-3 ring-1 ring-slate-200">
                Contact support if the same page continues to show this error.
              </li>
            </ul>
          </aside>
        </div>
      </section>
    </main>
  );
}
